import { Box, Skeleton, Stack, VStack } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { MultiValue } from 'react-select';

import { SelectionListControls } from '../../../../../shared/selection-lists/components/SelectionListControls/SelectionListControls';
import {
  type Option,
  SelectionListSelect,
} from '../../../../../shared/selection-lists/components/SelectionListSelect/SelectionListSelect';
import { useSelectionListState } from '../../../../../shared/selection-lists/hooks/useSelectionListState';
import { shouldShowCurrentPreviousToggle } from '../../config/currentPreviousConfig';
import {
  type ListData,
  useSingleFilterData,
} from '../../contexts/FilterProvider';
import { useFilterState } from '../../hooks/useFilters';
import type { ListFilter, ListSelection } from '../../types/filters';
import { getNumericIdFromNestedId } from '../../utils/get-numeric-id-from-nested-id';
import { ToggleSelect } from '../ToggleSelect';
import { NotToggle } from './NotToggle';

function matchSelectionToOption(
  selection: ListSelection,
  options: Option[]
): Option | null {
  const matchById = options.find((o) => o.value === selection.id);
  if (matchById) return matchById;
  const numericId = getNumericIdFromNestedId(String(selection.id));
  if (numericId !== null) {
    const matchByNumeric = options.find(
      (o) => String(numericId) === String(o.value)
    );
    if (matchByNumeric) return matchByNumeric;
  }
  const matchByLabel = options.find((o) => o.label === selection.name);
  return matchByLabel ?? null;
}

interface ListFilterFormProps {
  filter: ListFilter;
  closeMenu: () => void;
  onSave?: (filter: ListFilter) => void;
  isNotEnabled?: boolean;
  onIsNotEnabledChange?: (isNotEnabled: boolean) => void;
  showNotToggle?: boolean;
}

export const ListFilterForm: React.FC<ListFilterFormProps> = ({
  filter,
  closeMenu,
  onSave,
  isNotEnabled: isNotEnabledProp,
  onIsNotEnabledChange,
  showNotToggle = false,
}) => {
  const data = useSingleFilterData(filter.id);
  const { updateFilter } = useFilterState(filter.id);
  const { isSelectionListsLoading } = useSelectionListState();
  const location = useLocation();

  const [selectedOptions, setSelectedOptions] = useState<Option[]>(() => {
    return (
      filter.selections?.map((selection) => ({
        value: selection.id,
        label: selection.name,
      })) || []
    );
  });
  const [isCurrent, setIsCurrent] = useState<boolean>(filter.isCurrent ?? true);
  // isNotEnabled: whether the "not" logic is currently enabled (use prop if provided, otherwise filter value, default to false)
  const isNotEnabled = isNotEnabledProp ?? filter.isNot ?? false;

  const selectionList = (data as ListData[])?.[0]?.selectionList;
  const options: Option[] = useMemo(
    () =>
      selectionList?.map((item) => ({
        value: item.id,
        label: item.longName || item.shortName || item.id,
      })) ?? [],
    [selectionList]
  );

  // Intentionally omit selectedOptions — effect syncs filter.selections → display when options load; including it causes infinite loop when AI-generated IDs don't match options.
  // biome-ignore lint/correctness/useExhaustiveDependencies: sync filter.selections → selectedOptions when options load; selectedOptions omitted to avoid infinite loop.
  useEffect(() => {
    if (options.length === 0 || !filter.selections?.length) return;
    const needsNormalize = selectedOptions.some(
      (s) => !options.some((o) => o.value === s.value)
    );
    if (!needsNormalize) return;
    const normalized = filter.selections
      .map((selection) => {
        const option = matchSelectionToOption(selection, options);
        return (
          option ?? ({ value: selection.id, label: selection.name } as Option)
        );
      })
      .filter(
        (opt, idx, arr) => arr.findIndex((o) => o.value === opt.value) === idx
      );
    setSelectedOptions(normalized);
  }, [options, filter.selections]);

  const handleSelectionChange = (newValue: MultiValue<Option>) => {
    setSelectedOptions([...newValue]);
  };

  const handleSubmit = () => {
    const selections: ListSelection[] = selectedOptions.map((option) => ({
      id: option.value,
      name: option.label,
    }));

    const updatedFilter = {
      ...filter,
      selections,
      isCurrent,
      isNot: isNotEnabled,
      isUserSubmitted: true,
    };

    // Use onSave prop if provided (for MULTI filter support), otherwise fall back to updateFilter
    if (onSave) {
      onSave(updatedFilter);
    } else {
      updateFilter(updatedFilter);
    }
    closeMenu();
  };

  const handleClear = () => {
    setSelectedOptions([]);
  };

  const showToggle = shouldShowCurrentPreviousToggle(
    filter.id,
    location.pathname
  );

  if (isSelectionListsLoading && options.length === 0) {
    return (
      <Stack spacing={2} p={4}>
        <Skeleton height="20px" />
        <Skeleton height="20px" />
        <Skeleton height="20px" />
      </Stack>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <VStack position="relative" spacing={2} align="stretch">
        {showNotToggle && onIsNotEnabledChange && (
          <Box position="absolute" top="34px">
            <NotToggle
              filterName={filter.name}
              isNot={isNotEnabled}
              onChange={onIsNotEnabledChange}
            />
          </Box>
        )}
        <SelectionListSelect
          options={options}
          value={selectedOptions}
          maxMenuHeight={showNotToggle ? 180 : 220}
          onChange={handleSelectionChange}
          isMulti
          customStyles={{
            menu: showNotToggle
              ? {
                  margin: '2rem 0 0',
                }
              : {},
          }}
        />
        {showToggle && (
          <ToggleSelect
            filterName={filter.name}
            onChange={(e: number) => setIsCurrent(e === 0)}
            initialIsCurrent={isCurrent}
          />
        )}
      </VStack>
      <SelectionListControls
        onClear={handleClear}
        onSubmit={handleSubmit}
        onClose={closeMenu}
        submitText={filter.selections?.length > 0 ? 'Update' : 'Add'}
        isSubmitDisabled={selectedOptions.length === 0}
      />
    </VStack>
  );
};
